import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector, useDispatch } from "react-redux";
import { Select } from "antd";
import { ReactComponent as DownArrow } from "../../Assets/Icons/greyDownArrow.svg";
import { getRedeemedFrequencies } from "../../services/merchantService";

const displayFilters = {
  lastWeek: "Last Week",
  lastMonth: "Last Month",
  lastYear: "Last Year",
};

const AveragePurchaseFrequency = () => {
  const dispatch = useDispatch();
  const { redemptionFrequencies } = useSelector(
    (state) => state?.merchantReducer,
  );
  const [selectedFilter, setSelectedFilter] = useState("lastYear");
  const [filteredSeries, setFilteredSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [maxDataValue, setMaxDataValue] = useState(10);
  const [step, setStep] = useState(2);
  const [loading, setLoading] = useState(true);
  const [chartOptions, setChartOptions] = useState({
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
    },
    colors: ["#1E90FF", "#FF6347", "#32CD32"],
    chart: {
      fontFamily: "Poppins, sans-serif",
      height: 335,
      type: "bar",
      toolbar: { show: false },
    },
    responsive: [
      {
        breakpoint: 575,
        options: {
          xaxis: {
            labels: {
              rotate: -45,
              style: {
                fontSize: "10px",
              },
            },
          },
          yaxis: {
            labels: {
              style: {
                fontSize: "10px",
              },
            },
          },
        },
      },
    ],
    dataLabels: { enabled: false },
    plotOptions: {
      bar: {
        columnWidth: "40%",
      },
    },
    xaxis: {
      type: "category",
      categories: [],
      title: {
        text: "Time Period (Last Year)",
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      min: 0,
      max: 10,
      tickAmount: 5,
      labels: {
        formatter: (value) => value,
      },
      title: {
        text: "Redeemed Frequency",
        style: {
          fontSize: "14px",
          fontWeight: "400",
          color: "#373d3f",
          fontFamily: "Poppins, sans-serif",
        },
      },
    },
    tooltip: { shared: true, intersect: false },
  });

  const formatDate = (dateString) => {
    if (!dateString) return "";
    if (typeof dateString !== "string") return String(dateString);
    const date = new Date(dateString);
    if (dateString.includes("-") && dateString.split("-").length === 3) {
      if (isNaN(date.getTime())) return dateString;
      const month = date
        .toLocaleString("default", { month: "short" })
        .toUpperCase();
      const day = date.getDate();
      return `${day} ${month}`;
    } else {
      const parts = dateString.split("-");
      const year = parts[0] || "2026";
      const month = parts[1] || "01";
      const parsedDate = new Date(`${year}-${month}-01`);
      if (isNaN(parsedDate.getTime())) return dateString;
      const monthAbbr = parsedDate
        .toLocaleString("default", { month: "short" })
        .toUpperCase();
      return `${monthAbbr} ${year}`;
    }
  };

  const processFrequencyData = (frequencyData) => {
    if (!frequencyData || !frequencyData.success || !frequencyData.data?.data) {
      setFilteredSeries([]);
      setCategories([]);
      setLoading(false);
      return;
    }

    const { data } = frequencyData.data;

    if (!data || data.length === 0) {
      setFilteredSeries([]);
      setCategories([]);
      setLoading(false);
      return;
    }

    let finalCategories = [];
    let finalFrequencies = [];

    if (selectedFilter === "lastWeek") {
      const uniquePeriods = new Set();
      data.forEach((item) => {
        uniquePeriods.add(item.period);
      });
      const sortedPeriods = Array.from(uniquePeriods).sort();

      finalFrequencies = sortedPeriods.map((period) => {
        const found = data.find((d) => d.period === period);
        return found ? parseFloat(found.redeemedFrequency) : 0;
      });
      finalCategories = sortedPeriods.map(formatDate);
    } else if (selectedFilter === "lastMonth") {
      const formatWeekDateRange = (start, end) => {
        const startDay = start.getDate();
        const endDay = end.getDate();
        const startMonth = start.toLocaleString("default", { month: "short" });
        const endMonth = end.toLocaleString("default", { month: "short" });
        if (startMonth === endMonth) {
          return `(${startDay}-${endDay} ${startMonth})`;
        } else {
          return `(${startDay} ${startMonth} - ${endDay} ${endMonth})`;
        }
      };

      const weeks = [];
      const currentDate = new Date();
      for (let i = 0; i < 4; i++) {
        const daysInWeek = i === 3 ? 9 : 7;
        const end = new Date(currentDate);
        end.setDate(currentDate.getDate() - i * 7);
        const start = new Date(currentDate);
        start.setDate(currentDate.getDate() - i * 7 - daysInWeek + 1);

        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);

        weeks.unshift({
          label: `W${4 - i}`,
          start,
          end,
        });
      }

      const weekData = weeks.map((week) => {
        let totalCount = 0;
        let totalUsers = 0;

        data.forEach((item) => {
          const parts = item.period.split("-");
          if (parts.length === 3) {
            const year = parseInt(parts[0]);
            const month = parseInt(parts[1]) - 1;
            const day = parseInt(parts[2]);
            const itemDate = new Date(year, month, day);
            itemDate.setHours(0, 0, 0, 0);

            if (itemDate >= week.start && itemDate <= week.end) {
              totalCount += item.redemptionCount || 0;
              totalUsers += item.uniqueUserCount || 0;
            }
          }
        });

        const frequency =
          totalUsers > 0 ? Math.ceil(totalCount / totalUsers) : 0;
        return {
          periodLabel: [week.label, formatWeekDateRange(week.start, week.end)],
          frequency,
        };
      });

      finalFrequencies = weekData.map((w) => w.frequency);
      finalCategories = weekData.map((w) => w.periodLabel);
    } else {
      const uniquePeriods = new Set();
      data.forEach((item) => {
        uniquePeriods.add(item.period);
      });
      let sortedPeriods = Array.from(uniquePeriods).sort((a, b) => {
        const partsA = String(a).split("-").map(Number);
        const partsB = String(b).split("-").map(Number);
        if (partsA[0] !== partsB[0]) return partsA[0] - partsB[0];
        if (partsA[1] !== partsB[1]) return partsA[1] - partsB[1];
        if (partsA[2] !== undefined && partsB[2] !== undefined && partsA[2] !== partsB[2]) {
          return partsA[2] - partsB[2];
        }
        return 0;
      });
      sortedPeriods = sortedPeriods.slice(-6);

      finalFrequencies = sortedPeriods.map((period) => {
        const found = data.find((d) => d.period === period);
        return found ? parseFloat(found.redeemedFrequency) : 0;
      });
      finalCategories = sortedPeriods.map(formatDate);
    }

    const newMax = Math.max(...finalFrequencies, 5);
    const newStep = Math.ceil(newMax / 5);

    setMaxDataValue(newMax);
    setStep(newStep);

    const series = [
      {
        name: "Redeemed Frequency",
        data: finalFrequencies,
      },
    ];

    setCategories(finalCategories);
    setFilteredSeries(series);

    setChartOptions((prev) => ({
      ...prev,
      xaxis: { ...prev.xaxis, categories: finalCategories },
      yaxis: {
        ...prev.yaxis,
        max: Math.ceil(newMax / newStep) * newStep,
        tickAmount: Math.ceil(newMax / newStep),
      },
      plotOptions: {
        ...prev.plotOptions,
        bar: {
          columnWidth: `${Math.min(100 / (finalCategories.length + 1), 50)}%`,
        },
      },
    }));

    setLoading(false);
  };

  const handleFilterChange = (filterValue) => {
    setSelectedFilter(filterValue);
    setLoading(true);
    dispatch(getRedeemedFrequencies({ timePeriod: filterValue }));

    setChartOptions((prev) => ({
      ...prev,
      xaxis: {
        ...prev.xaxis,
        title: {
          text: `Time Period (${displayFilters[filterValue] || "Last Year"})`,
          style: {
            fontSize: "14px",
            fontWeight: "400",
            color: "#373d3f",
            fontFamily: "Poppins, sans-serif",
          },
        },
      },
    }));
  };

  useEffect(() => {
    if (redemptionFrequencies) {
      processFrequencyData(redemptionFrequencies);
    }
  }, [redemptionFrequencies]);

  const isLastWeek = selectedFilter === "lastWeek";

  return (
    <div className="rounded-sm border border-stroke bg-white px-5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 summary offersChart">
      <div className="flex flex-col gap-4 xl:flex-row xl:gap-0 justify-between w-full mt-4">
        <span className="text-[18px] sm:text-[20px] font-poppins font-[600] text-[black] leading-tight flex-1">
          Average Purchase Frequency
        </span>
        <Select
          value={selectedFilter}
          className="border border-[#00ADEF] rounded-md pr-3 text-lg font-medium outline-none h-[40px]"
          suffixIcon={<DownArrow />}
          options={[
            { label: "Last Week", value: "lastWeek" },
            { label: "Last Month", value: "lastMonth" },
            { label: "Last Year", value: "lastYear" },
          ]}
          defaultValue="lastYear"
          onChange={handleFilterChange}
          dropdownMatchSelectWidth={false}
          style={{ minWidth: "100px", maxWidth: "200px" }}
        />
      </div>
      <div className="w-full flex items-center justify-center">
        <div id="chartTwo" className="mt-4 w-full h-[430px]">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500 text-lg font-medium">
                Loading...
              </div>
            </div>
          ) : filteredSeries[0]?.data.length > 0 &&
            filteredSeries[0]?.data?.length > 0 ? (
            <ReactApexChart
              options={chartOptions}
              series={filteredSeries}
              type="bar"
              height={410}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500 text-lg font-medium">
                No Purchase Frequency Data Found
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AveragePurchaseFrequency;
